import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';

import { type InstanceShortDataType } from '@/api/organization';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { Panel } from '@/components/panels';
import { MonthDateYearFormat } from '@/constants/formats';
import { formatDate } from '@/helper/dateHelper';

type Props = {
  selectedRow: InstanceShortDataType | undefined;
  data?: InstanceShortDataType[];
  totalResults?: number;
  isLoading?: boolean;
  error: Error | null;
  onSelectedRowChange: (org: InstanceShortDataType) => void;
};

const LoginAsTable = ({
  selectedRow,
  data,
  totalResults,
  isLoading,
  error,
  onSelectedRowChange,
}: Props) => {
  const columns = useMemo<InstantSearchDataTableColumnDef<InstanceShortDataType>[]>(
    () => [
      {
        header: t`Name`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: true,
        minSize: 160,
        flex: 1,
        cell: ({ row }) => row.original.name,
      },
      {
        header: t`Domain`,
        id: 'domain',
        minSize: 140,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.domain,
      },
      {
        header: t`Instance Id`,
        id: 'id',
        minSize: 200,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.id,
      },
      {
        header: t`Created On`,
        id: 'createdAt',
        accessorKey: 'createdAt',
        meta: { align: 'end' },
        enableSorting: true,
        size: 140,
        cell: ({ row }) => formatDate(row.original.createdAt, MonthDateYearFormat),
      },
    ],
    [],
  );

  const handleSelectedRowIdsChange = useCallback(
    (selectedRowIds: string[]) => {
      const id = selectedRowIds[0];
      if (id == null) {
        return;
      }
      const row = data?.find((r) => r.id === id);
      if (row != null) {
        onSelectedRowChange(row);
      }
    },
    [data, onSelectedRowChange],
  );

  return (
    <Panel title={t`Customer Instances`}>
      <InstantSearchDataTable<InstanceShortDataType>
        columns={columns}
        data={data}
        totalResults={totalResults}
        loading={isLoading}
        emptyMessage={<Trans>No customer instances found.</Trans>}
        error={error}
        getRowId={(record) => record.id}
        initialColumnPinning={{
          left: ['name'],
        }}
        rowSelection={{
          type: 'radio',
          selectedRowIds: selectedRow ? [selectedRow.id] : [],
          onSelectedRowIdsChange: handleSelectedRowIdsChange,
        }}
        rowCursor="pointer"
        onRow={(record) => ({ onClick: () => onSelectedRowChange(record) })}
      />
    </Panel>
  );
};

export default LoginAsTable;
